import {Container, ContainerModule, interfaces} from "inversify";
import {Bindings} from "data/constants/bindings";
import {IUserStore, UserStore} from "data/stores/user/user.store";
import {AuthController, IAuthController} from "views/controllers/auth/auth.controller";
import {
	IMyAccountController,
	MyAccountController,
} from "views/pages/my_account/my_account.controller";
import {API_URL, JSON_URL} from "data/constants";
import {IJSONProvider, JSONProvider} from "data/providers/json/json.provider";
import {AuthApiProvider, IAuthApiProvider} from "data/providers/api/auth.api.provider";
import {HttpClientFactory, IHttpClientService} from "data/services/http";
import {CountriesStore, ICountriesStore} from "data/stores/countries/countries.store";
import {RoundsStore, IRoundsStore} from "data/stores/rounds/rounds.store";
import {PlayersStore, IPlayersStore} from "data/stores/players/players.store";
import {SquadsStore, ISquadsStore} from "data/stores/squads/squds.store";
import {IHOCMainController, HOCMainController} from "views/components/HOCMain/HOCMain.controller";
import {IModalController, ModalController} from "views/components/modals/modal.controller";
import {IModalsStore, ModalsStore} from "data/stores/modals/modals.store";
import {IStaticStore, StaticStore} from "data/stores/static/static.store";
import {IHelpController, HelpController} from "views/pages/help/help.controller";
import {ILeaguesApiProvider, LeaguesApiProvider} from "data/providers/api/leagues.api.provider";
import {ILeaguesStore, LeaguesStore} from "data/stores/leagues/leagues.store";
import {
	IFormLeagueCreateController,
	FormLeagueCreateController,
} from "views/components/FormLeagueCreate/formLeagueCreate.controller";
import {
	IMyLeaguesController,
	MyLeaguesController,
} from "views/pages/leagues/my_leagues/my_leagues.controller";
import {
	IInviteLeagueController,
	InviteLeagueController,
} from "views/pages/league/league_invites/league_invites.controller";
import {
	IShareControllerController,
	ShareControllerController,
} from "views/components/Share/share.controller";

import {
	ILeaguesJoinController,
	LeaguesJoinController,
} from "views/pages/leagues/join/leagues_join.controller";

import {
	IJoinLeaguesListItemController,
	JoinLeaguesListItemController,
} from "views/components/JoinLeaguesListItem/JoinLeaguesListItem.controller";

import {IPicksController, PicksController} from "views/pages/picks/picks.controller";

import {IPredictionsStore, PredictionsStore} from "data/stores/predictions/predictions.store";
import {
	IPickItemController,
	PickItemController,
} from "views/components/PickItem/pickItem.controller";
import {
	IPredictionApiProvider,
	PredictionApiProvider,
} from "data/providers/api/predictions.api.provider";
import {
	IPickStickyBarController,
	PickStickyBarController,
} from "views/components/PickStickyBar/pickStickyBar.controller";

import {ICrunchApiProvider, CrunchApiProvider} from "data/providers/api/сrunch.api.provider";
import {IRankingsApiProvider, RankingsApiProvider} from "data/providers/api/rankings.api.provider";

import {ICrunchStore, CrunchStore} from "data/stores/crunch/crunch.store";
import {IRankingsStore, RankingsStore} from "data/stores/rankings/rankings.store";

import {ICrunchController, CrunchController} from "views/components/Crunch/crunch.controller";
import {IGameBarController, GameBarController} from "views/components/GameBar/gamebar.controller";
import {IStatsController, StatsController} from "views/components/Stats/stats.controller";
import {
	ILeaderboardPageController,
	LeaderboardPageController,
} from "views/pages/leaderboard/leaderboard.controller";

import {
	ILeagueLadderController,
	LeagueLadderController,
} from "views/pages/league/league_ladder/leagueLadder.controller";
import {
	IViewLeagueOutletController,
	ViewLeagueOutletController,
} from "views/pages/league/viewLeague.controller";

import {
	ILeagueAboutController,
	LeagueAboutController,
} from "views/pages/league/league_about/leagueAbout.controller";

import {
	ILeagueManageController,
	LeagueManageController,
} from "views/pages/league/league_manage/leagueManage.controller";

import {
	IFormLeagueUpdateController,
	FormLeagueUpdateController,
} from "views/components/FormLeagueUpdate/formLeagueUpdate.controller";

import {
	IManageTableController,
	ManageTableController,
} from "views/components/ManageTable/manageTable.controller";

import {
	IModalTutorialController,
	ModalTutorialController,
} from "views/components/modals/ModalTutorial/modalTutorial.controller";
import {
	ISliderScreenController,
	SliderScreenController,
} from "views/components/modals/ModalTutorial/SliderScreen/sliderScreen.controller";

import {IProfileController, ProfileController} from "views/pages/profile/profile.controller";

import {ISharingApiProvider, SharingApiProvider} from "data/providers/api/sharing.api.provider";
import {
	IPredictionsShareModalController,
	PredictionsShareModalController,
} from "views/components/modals/PredictionsShareModal/predictionShareModal.controller";

import {
	InvitePageController,
	IInvitePageController,
} from "views/pages/invite/invite_page.controller";

export const DIContainer = new Container();

export const services = new ContainerModule((bind: interfaces.Bind) => {
	bind<IHttpClientService>(Bindings.ApiHTTPClient).toConstantValue(
		HttpClientFactory.createApiClient({
			baseURL: API_URL,
			withCredentials: true,
		})
	);

	bind<IHttpClientService>(Bindings.JsonHTTPClient).toConstantValue(
		HttpClientFactory.createJSONClient({
			baseURL: JSON_URL,
		})
	);
});

export const providers = new ContainerModule((bind: interfaces.Bind) => {
	bind<IJSONProvider>(Bindings.JSONProvider).to(JSONProvider);
	bind<IAuthApiProvider>(Bindings.AuthApiProvider).to(AuthApiProvider);
	bind<ILeaguesApiProvider>(Bindings.LeaguesApiProvider).to(LeaguesApiProvider);
	bind<IPredictionApiProvider>(Bindings.PredictionApiProvider).to(PredictionApiProvider);
	bind<ICrunchApiProvider>(Bindings.CrunchApiProvider).to(CrunchApiProvider);
	bind<IRankingsApiProvider>(Bindings.RankingsApiProvider).to(RankingsApiProvider);
	bind<ISharingApiProvider>(Bindings.SharingApiProvider).to(SharingApiProvider);
});

export const stores = new ContainerModule((bind: interfaces.Bind) => {
	bind<IUserStore>(Bindings.UserStore).to(UserStore).inSingletonScope();
	bind<ICountriesStore>(Bindings.CountriesStore).to(CountriesStore).inSingletonScope();
	bind<IRoundsStore>(Bindings.RoundsStore).to(RoundsStore).inSingletonScope();
	bind<IPlayersStore>(Bindings.PlayersStore).to(PlayersStore).inSingletonScope();
	bind<ISquadsStore>(Bindings.SquadsStore).to(SquadsStore).inSingletonScope();
	bind<IModalsStore>(Bindings.ModalsStore).to(ModalsStore).inSingletonScope();
	bind<IStaticStore>(Bindings.StaticStore).to(StaticStore).inSingletonScope();
	bind<ILeaguesStore>(Bindings.LeaguesStore).to(LeaguesStore).inSingletonScope();
	bind<IPredictionsStore>(Bindings.PredictionsStore).to(PredictionsStore).inSingletonScope();
	bind<ICrunchStore>(Bindings.CrunchStore).to(CrunchStore).inSingletonScope();
	bind<IRankingsStore>(Bindings.RankingsStore).to(RankingsStore).inSingletonScope();
});

export const controllers = new ContainerModule((bind: interfaces.Bind) => {
	bind<IAuthController>(Bindings.AuthController).to(AuthController);
	bind<IMyAccountController>(Bindings.MyAccountController).to(MyAccountController);
	bind<IHOCMainController>(Bindings.HOCMainController).to(HOCMainController);
	bind<IModalController>(Bindings.ModalController).to(ModalController);
	bind<IHelpController>(Bindings.HelpController).to(HelpController);
	bind<IMyLeaguesController>(Bindings.MyLeaguesController).to(MyLeaguesController);
	bind<IFormLeagueCreateController>(Bindings.FormLeagueCreateController).to(
		FormLeagueCreateController
	);
	bind<IInviteLeagueController>(Bindings.InviteLeagueController).to(InviteLeagueController);
	bind<IShareControllerController>(Bindings.ShareControllerController).to(
		ShareControllerController
	);
	bind<ILeaguesJoinController>(Bindings.LeaguesJoinController).to(LeaguesJoinController);
	bind<IJoinLeaguesListItemController>(Bindings.JoinLeaguesListItemController).to(
		JoinLeaguesListItemController
	);
	bind<IPicksController>(Bindings.PicksController).to(PicksController);
	bind<IPickItemController>(Bindings.PickItemController).to(PickItemController);
	bind<IPickStickyBarController>(Bindings.PickStickyBarController).to(PickStickyBarController);
	bind<ICrunchController>(Bindings.CrunchController).to(CrunchController);
	bind<IGameBarController>(Bindings.GameBarController).to(GameBarController);
	bind<IStatsController>(Bindings.StatsController).to(StatsController);
	bind<ILeaderboardPageController>(Bindings.LeaderboardPageController).to(
		LeaderboardPageController
	);
	bind<ILeagueLadderController>(Bindings.LeagueLadderController).to(LeagueLadderController);
	bind<IViewLeagueOutletController>(Bindings.ViewLeagueOutletController).to(
		ViewLeagueOutletController
	);
	bind<ILeagueAboutController>(Bindings.LeagueAboutController).to(LeagueAboutController);
	bind<ILeagueManageController>(Bindings.LeagueManageController).to(LeagueManageController);
	bind<IFormLeagueUpdateController>(Bindings.FormLeagueUpdateController).to(
		FormLeagueUpdateController
	);
	bind<IManageTableController>(Bindings.ManageTableController).to(ManageTableController);
	bind<IModalTutorialController>(Bindings.ModalTutorialController).to(ModalTutorialController);
	bind<ISliderScreenController>(Bindings.SliderScreenController).to(SliderScreenController);
	bind<IProfileController>(Bindings.ProfileController).to(ProfileController);
	bind<IPredictionsShareModalController>(Bindings.PredictionsShareModalController).to(
		PredictionsShareModalController
	);
	bind<IInvitePageController>(Bindings.InvitePageController).to(InvitePageController);
});
